import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Sparkles, Trophy, Activity, ArrowUpRight, MessageSquare } from "lucide-react";
import { adminApi } from "./api";
import { usePageMeta } from "../hooks/usePageMeta";

const fmtDate = (iso) => {
  try {
    const d = typeof iso === "string" ? new Date(iso) : new Date();
    return d.toLocaleString("es-GT", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "";
  }
};

export default function AdminDashboard() {
  usePageMeta({ title: "Dashboard · Daos CRM" });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    adminApi
      .get("/admin/stats")
      .then((r) => alive && setStats(r.data))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="container-x py-8 lg:py-12">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-3">CRM</div>
          <h1 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter">
            Dashboard
          </h1>
          <p className="mt-2 text-zinc-400 text-sm">Resumen en tiempo real de tu pipeline comercial.</p>
        </div>
        <Link to="/admin/leads" className="btn-primary !py-2.5 !px-5 text-sm" data-testid="dashboard-go-leads">
          Ver todos los leads <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <Kpi title="Total leads" value={stats?.total ?? "—"} icon={Inbox} loading={loading} testid="kpi-total" />
        <Kpi title="Nuevos" value={stats?.new ?? "—"} icon={Sparkles} loading={loading} testid="kpi-new" />
        <Kpi title="Cerrados ganados" value={stats?.won ?? "—"} icon={Trophy} loading={loading} testid="kpi-won" />
        <Kpi title="Score promedio" value={stats ? `${stats.avg_score}/100` : "—"} icon={Activity} loading={loading} testid="kpi-avg" />
      </div>

      {/* Distribution panels */}
      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <DistroCard title="Por servicio" rows={stats?.by_service} loading={loading} testid="distro-service" />
        <DistroCard title="Por fuente" rows={stats?.by_source} loading={loading} testid="distro-source" />
        <DistroCard title="Por estado" rows={stats?.by_status} loading={loading} testid="distro-status" />
      </div>

      {/* Conversion by landing */}
      <div className="mt-6 grad-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-white text-lg">Conversiones por landing</h2>
          <span className="text-xs text-zinc-500">Top páginas de origen</span>
        </div>
        {loading ? (
          <Skeleton lines={4} />
        ) : (
          <div className="space-y-2">
            {(stats?.by_landing || []).length === 0 && <p className="text-zinc-500 text-sm">Aún no hay datos.</p>}
            {(stats?.by_landing || []).map((r) => {
              const max = Math.max(...(stats?.by_landing || []).map((x) => x.count), 1);
              return (
                <div key={r.label} className="flex items-center gap-3">
                  <div className="text-zinc-300 text-sm font-display w-44 truncate">{r.label}</div>
                  <div className="flex-1 h-2 rounded-full bg-white/[0.04] overflow-hidden">
                    <div className="h-full bg-gradient-brand" style={{ width: `${(r.count / max) * 100}%` }} />
                  </div>
                  <div className="text-zinc-400 text-sm w-8 text-right">{r.count}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent leads */}
      <div className="mt-6 grad-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-white text-lg">Leads recientes</h2>
          <Link to="/admin/leads" className="text-xs text-[#ff8a00] font-display tracking-[0.18em] uppercase">
            Ver todos →
          </Link>
        </div>
        {loading ? (
          <Skeleton lines={6} />
        ) : (
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="w-full text-sm" data-testid="dashboard-recent-leads">
              <thead>
                <tr className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">
                  <th className="text-left font-display font-semibold py-2 px-2">Nombre</th>
                  <th className="text-left font-display font-semibold py-2 px-2 hidden sm:table-cell">Empresa</th>
                  <th className="text-left font-display font-semibold py-2 px-2 hidden md:table-cell">Servicio</th>
                  <th className="text-left font-display font-semibold py-2 px-2 hidden lg:table-cell">Fuente</th>
                  <th className="text-left font-display font-semibold py-2 px-2">Score</th>
                  <th className="text-left font-display font-semibold py-2 px-2 hidden md:table-cell">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.recent || []).length === 0 && (
                  <tr><td colSpan={6} className="text-zinc-500 text-sm py-6">Aún no llegan leads.</td></tr>
                )}
                {(stats?.recent || []).map((l) => (
                  <tr key={l.id} className="border-t border-white/[0.05]">
                    <td className="py-3 px-2">
                      <div className="text-white font-display font-semibold">{l.full_name || "—"}</div>
                      <div className="text-zinc-500 text-xs">{l.email}</div>
                    </td>
                    <td className="py-3 px-2 text-zinc-300 hidden sm:table-cell">{l.company_name || "—"}</td>
                    <td className="py-3 px-2 text-zinc-300 hidden md:table-cell">{l.service_interest || "—"}</td>
                    <td className="py-3 px-2 text-zinc-300 hidden lg:table-cell">{l.lead_source || "—"}</td>
                    <td className="py-3 px-2"><ScoreBadge value={l.lead_score || 0} /></td>
                    <td className="py-3 px-2 text-zinc-500 text-xs hidden md:table-cell">{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ title, value, icon: Icon, loading, testid }) {
  return (
    <div className="grad-border p-5 sm:p-6 card-lift" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00] font-display font-semibold">{title}</div>
        <Icon className="w-4 h-4 text-[#ff8a00]" />
      </div>
      <div className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-white tracking-tighter">
        {loading ? <span className="inline-block w-16 h-8 bg-white/[0.04] rounded animate-pulse" /> : value}
      </div>
    </div>
  );
}

function DistroCard({ title, rows = [], loading, testid }) {
  return (
    <div className="grad-border p-6" data-testid={testid}>
      <h2 className="font-display font-bold text-white text-lg mb-4">{title}</h2>
      {loading ? (
        <Skeleton lines={4} />
      ) : rows.length === 0 ? (
        <p className="text-zinc-500 text-sm">Aún no hay datos.</p>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r) => {
            const max = Math.max(...rows.map((x) => x.count), 1);
            return (
              <div key={r.label} className="flex items-center gap-3 text-sm">
                <div className="text-zinc-300 truncate flex-1">{r.label}</div>
                <div className="w-28 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                  <div className="h-full bg-gradient-brand" style={{ width: `${(r.count / max) * 100}%` }} />
                </div>
                <div className="text-zinc-400 w-7 text-right tabular-nums">{r.count}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ScoreBadge({ value }) {
  const n = Math.max(0, Math.min(100, value));
  const tier = n >= 60 ? "high" : n >= 30 ? "mid" : "low";
  const cls = {
    high: "bg-gradient-brand text-black",
    mid: "bg-white/[0.06] text-white border border-[#ff8a00]/30",
    low: "bg-white/[0.04] text-zinc-400 border border-white/[0.06]",
  }[tier];
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-display font-bold ${cls}`}>
      {n}
    </span>
  );
}

function Skeleton({ lines = 3 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="h-3 rounded bg-white/[0.04] animate-pulse" style={{ width: `${70 + Math.random() * 25}%` }} />
      ))}
    </div>
  );
}
